/**
 * `createBackend()` — the primary factory for the unified Backend interface.
 *
 * Today this returns the remote backend unconditionally. The demo backend
 * (SQLite-WASM in OPFS) was deferred post-MVP per the 2026-05-07
 * architectural review (T3.1); the factory shape is preserved so a future
 * demo / offline-first variant can slot back in without callers changing.
 *
 * Spec ref: architecture.md §4.22 — DraftService seam + commit protocol.
 */

import type { Backend, AuthObserver, BackendMode } from "./types.js";
import { createRemoteBackend } from "./remote-backend.js";

export interface CreateBackendOptions {
  /**
   * Explicit mode override. Today only `"remote"` is supported — kept as an
   * option for parity with the future shape.
   */
  mode?: BackendMode;

  /** Auth observer — currently unused; preserved for forward compat. */
  authObserver?: AuthObserver;

  /** oRPC API origin for remote mode. */
  apiBaseUrl?: string;

  /** y-partyserver origin for remote mode (defaults to apiBaseUrl). */
  syncBaseUrl?: string;

  /**
   * Callback invoked when the backend mode changes. Currently never fires
   * (demo mode deferred); preserved for API stability.
   */
  onModeChange?: (backend: Backend) => void;
}

export interface BackendHandle {
  /** The currently-active backend. */
  current: Backend;
  /**
   * Tear down auth subscriptions and release resources.
   * Call this on component unmount / app shutdown.
   */
  destroy(): void;
}

/**
 * Construct the (remote) backend.
 *
 * Returns a `BackendHandle` so callers retain the same shape they had when
 * demo / remote selection was dynamic.
 *
 * Example (SPA root):
 * ```ts
 * const handle = createBackend({ apiBaseUrl });
 * // handle.current is the remote backend.
 * ```
 */
export function createBackend(opts: CreateBackendOptions = {}): BackendHandle {
  const apiBaseUrl = opts.apiBaseUrl ?? "";
  const syncBaseUrl = opts.syncBaseUrl ?? apiBaseUrl;
  const currentBackend = createRemoteBackend({ apiBaseUrl, syncBaseUrl });

  return {
    get current() {
      return currentBackend;
    },
    destroy() {
      // No-op — no auth subscription wired in remote-only mode.
    },
  };
}
